'use client'

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { useRedditConnection } from '@/hooks/useRedditConnection'
import { 
  Bot, 
  Brain, 
  Zap, 
  Target, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Settings, 
  Search,
  Plus,
  ExternalLink,
  Users,
  MessageSquare,
  Shield,
  Sparkles,
  Globe,
  ArrowRight,
  Check,
  X,
  RefreshCw,
  Eye,
  ThumbsUp,
  Calendar,
  Wand2,
  ArrowLeft,
  Save,
  Loader2,
  Trash2
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

interface RedditConnection {
  isConnected: boolean
  username?: string
  karma: number
  accountAge: string
  redditId?: string
  accessToken?: string
  refreshToken?: string
}

interface SubredditCandidate {
  id: string
  name: string
  subscribers: number
  description: string
  difficulty: 'Easy' | 'Medium' | 'Hard'
  requirements: string[]
  isApproved: boolean
  validationStatus: 'valid' | 'insufficient_karma' | 'not_subscribed' | 'pending' | 'checking'
  lastChecked?: string
}

interface PostingSlot {
  day: string
  time: string
  isActive: boolean
  timezone?: string
}

interface AgentSettings {
  requiresApproval: boolean
  autoSubscribe: boolean
  postFrequency: string
  maxPostsPerDay: number
  pauseOnErrors: boolean
}

export default function RedditAgentSetup() {
  const { id: projectId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { isConnected, redditAccount, isLoading: isLoadingConnection } = useRedditConnection()

  const [currentStep, setCurrentStep] = useState(1)
  const [isSaving, setIsSaving] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const [subredditCandidates, setSubredditCandidates] = useState<SubredditCandidate[]>([])
  const [postingSlots, setPostingSlots] = useState<PostingSlot[]>([
    { day: 'Monday', time: '09:00', isActive: false, timezone: 'UTC' },
    { day: 'Tuesday', time: '14:00', isActive: false, timezone: 'UTC' },
    { day: 'Wednesday', time: '10:00', isActive: false, timezone: 'UTC' },
    { day: 'Thursday', time: '15:00', isActive: false, timezone: 'UTC' },
    { day: 'Friday', time: '11:00', isActive: false, timezone: 'UTC' },
    { day: 'Saturday', time: '13:00', isActive: false, timezone: 'UTC' },
    { day: 'Sunday', time: '16:00', isActive: false, timezone: 'UTC' }
  ])

  const [agentSettings, setAgentSettings] = useState<AgentSettings>({
    requiresApproval: true,
    autoSubscribe: true,
    postFrequency: 'daily',
    maxPostsPerDay: 3,
    pauseOnErrors: true
  })

  // Load existing setup data on component mount
  useEffect(() => {
    if (!projectId || !user?.id) return
    
    const loadSetupData = async () => {
      try {
        // Load existing agent settings
        const { data: project, error: projectError } = await supabase
          .from('projects')
          .select('enable_reddit_agent, agent_requires_post_approval, reddit_auto_subscribe')
          .eq('id', projectId)
          .single()
        
        if (projectError) {
          console.error('Error loading project settings:', projectError)
        } else if (project) {
          setAgentSettings(prev => ({
            ...prev,
            requiresApproval: project.agent_requires_post_approval !== false,
            autoSubscribe: project.reddit_auto_subscribe !== false
          }))
        }
        
        // Load approved subreddits
        const { data: subreddits, error: subredditError } = await supabase
          .from('approved_subreddits')
          .select('*')
          .eq('project_id', projectId)
          .eq('user_id', user.id)
        
        console.log('Loaded subreddits in setup:', subreddits); // Debug logging
        
        if (subredditError) {
          console.error('Error loading subreddits:', subredditError)
        } else if (subreddits && subreddits.length > 0) {
          setSubredditCandidates(subreddits.map((sub: any) => ({
            id: sub.id,
            name: sub.subreddit_name,
            subscribers: sub.subscriber_count || 0,
            description: sub.description || `${sub.subreddit_name} subreddit`,
            difficulty: sub.difficulty || 'Medium',
            requirements: sub.requirements || [],
            isApproved: true, // All records in approved_subreddits table are approved
            validationStatus: sub.validation_status || 'valid',
            lastChecked: sub.last_validated_at || sub.added_at
          })))
        } else {
          console.log('No approved subreddits found in database')
        }
        
        // Load posting slots
        const { data: slots, error: slotsError } = await supabase
          .from('reddit_posting_slots')
          .select('*')
          .eq('project_id', projectId)
          .eq('is_active', true)
        
        if (slotsError) {
          console.error('Error loading posting slots:', slotsError)
        } else if (slots && slots.length > 0) {
          const numberToDayName = {
            0: 'Sunday',
            1: 'Monday',
            2: 'Tuesday',
            3: 'Wednesday', 
            4: 'Thursday',
            5: 'Friday',
            6: 'Saturday'
          }

          // Update postingSlots state with loaded data, converting UTC back to local time
          setPostingSlots(prev => 
            prev.map(slot => {
              const dbSlot = slots.find((s: any) => 
                numberToDayName[s.day_of_week as keyof typeof numberToDayName] === slot.day
              )
              if (dbSlot) {
                // FIXED: Convert UTC time back to user's local time for display
                // The database stores UTC time (e.g., 17:00 UTC)
                // We need to convert it back to user's local time (e.g., 19:00 Amsterdam time)
                
                // Create a date object with UTC time
                const utcTimeString = dbSlot.time_slot_start_utc.substring(0, 5) // Get HH:MM from HH:MM:SS
                const [utcHours, utcMinutes] = utcTimeString.split(':').map(Number)
                
                // Create a date object for today with the UTC time
                const utcDateTime = new Date()
                utcDateTime.setUTCHours(utcHours, utcMinutes, 0, 0)
                
                // Convert to local time and extract local time components
                const localHours = utcDateTime.getHours()
                const localMinutes = utcDateTime.getMinutes()
                const localTimeString = `${localHours.toString().padStart(2, '0')}:${localMinutes.toString().padStart(2, '0')}`
                
                console.log(`Loading posting slot: ${utcTimeString} (UTC) → ${localTimeString} (local) for ${slot.day}`)
                
                return {
                  ...slot,
                  time: localTimeString, // Now properly converted to local time
                  isActive: true
                }
              }
              return slot
            })
          )
          
          // FIXED: Calculate posting frequency from loaded active slots
          const activeSlotCount = slots.length
          let calculatedFrequency = 'custom'
          
          if (activeSlotCount === 7) {
            calculatedFrequency = 'daily'
          } else if (activeSlotCount === 1) {
            calculatedFrequency = 'weekly'
          } else if (activeSlotCount >= 3 && activeSlotCount <= 4) {
            calculatedFrequency = 'every-other-day'
          }
          
          setAgentSettings(prev => ({
            ...prev,
            postFrequency: calculatedFrequency
          }))
          
          console.log(`Calculated posting frequency: ${calculatedFrequency} (${activeSlotCount} active slots)`)
        }
        
      } catch (error) {
        console.error('Error loading setup data:', error)
        toast({
          title: "Loading Error",
          description: "Failed to load existing setup data. You can still configure the agent.",
          variant: "destructive"
        })
      }
    }
    
    loadSetupData()
  }, [projectId, user?.id, isConnected])

  // Real Reddit connection function
  const connectReddit = async () => {
    if (!projectId) return
    
    try {
      // Generate OAuth URL for Reddit connection
      const state = crypto.randomUUID()
      const clientId = import.meta.env.VITE_REDDIT_CLIENT_ID
      
      if (!clientId || clientId === 'your_reddit_client_id') {
        toast({
          title: "Configuration Required",
          description: "Reddit client ID is not configured. Check the REDDIT_SETUP.md file for setup instructions.",
          variant: "destructive"
        })
        return
      }
      
      const redirectUri = `${window.location.origin}/auth/reddit/callback`
      const authUrl = `https://www.reddit.com/api/v1/authorize?` +
        `client_id=${clientId}&` +
        `response_type=code&` +
        `state=${state}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `duration=permanent&` +
        `scope=identity,submit,read,mysubreddits`
      
      // Store state and return URL for callback
      localStorage.setItem('reddit_oauth_state', state)
      localStorage.setItem('reddit_oauth_project_id', projectId)
      localStorage.setItem('reddit_oauth_return_url', window.location.href)
      
      // Redirect to Reddit OAuth
      window.location.href = authUrl
      
    } catch (error) {
      console.error('Error initiating Reddit connection:', error)
      toast({
        title: "Connection Error",
        description: "Failed to initiate Reddit connection. Please try again.",
        variant: "destructive"
      })
    }
  }

  // Save all settings function
  const saveSettings = async () => {
    if (!projectId || !user?.id) return
    
    setIsSaving(true)
    try {
      // Save basic agent settings to project using existing columns
      const { error: projectError } = await supabase
        .from('projects')
        .update({
          agent_requires_post_approval: agentSettings.requiresApproval,
          reddit_auto_subscribe: agentSettings.autoSubscribe
        })
        .eq('id', projectId)
      
      if (projectError) throw projectError
      
      // Save approved subreddits (Note: Records in approved_subreddits are already approved by definition)
      const approvedSubreddits = subredditCandidates.filter(sub => sub.isApproved)
      
      for (const subreddit of approvedSubreddits) {
        const { error: subredditError } = await supabase
          .from('approved_subreddits')
          .upsert({
            id: subreddit.id,
            project_id: projectId,
            user_id: user.id,
            subreddit_name: subreddit.name,
            validation_status: subreddit.validationStatus,
            last_validated_at: subreddit.lastChecked || new Date().toISOString(),
            allow_autoposting: true
          }, {
            onConflict: 'id'
          })
        
        if (subredditError) {
          console.error('Error saving subreddit:', subredditError)
        }
      }
      
      // Save posting slots
      // First, delete existing slots for this project
      const { error: deleteError } = await supabase
        .from('reddit_posting_slots')
        .delete()
        .eq('project_id', projectId)
      
      if (deleteError) {
        console.error('Error deleting existing slots:', deleteError)
      }

      // Then, insert new active slots with proper timezone conversion
      const activeSlots = postingSlots.filter(slot => slot.isActive)
      const dayNameToNumber = {
        'Monday': 1,
        'Tuesday': 2, 
        'Wednesday': 3,
        'Thursday': 4,
        'Friday': 5,
        'Saturday': 6,
        'Sunday': 0
      }

      for (const slot of activeSlots) {
        // FIXED: Properly convert user's local time to UTC
        // The user selects a time in their local timezone (e.g., 19:00 Amsterdam time)
        // We need to convert this to UTC before storing (e.g., 17:00 UTC)
        
        // Create a date object for today with the user's selected time
        const localDateTime = new Date()
        const [hours, minutes] = slot.time.split(':').map(Number)
        localDateTime.setHours(hours, minutes, 0, 0)
        
        // Convert to UTC and extract the UTC time components
        const utcHours = localDateTime.getUTCHours()
        const utcMinutes = localDateTime.getUTCMinutes()
        const utcTimeString = `${utcHours.toString().padStart(2, '0')}:${utcMinutes.toString().padStart(2, '0')}:00`
        
        console.log(`Converting posting slot: ${slot.day} ${slot.time} (local) → ${utcTimeString} (UTC)`)
        
        const { error: slotError } = await supabase
          .from('reddit_posting_slots')
          .insert({
            project_id: projectId,
            day_of_week: dayNameToNumber[slot.day as keyof typeof dayNameToNumber],
            time_slot_start_utc: utcTimeString, // Now properly converted to UTC
            is_active: true
          })
        
        if (slotError) {
          console.error('Error saving posting slot:', slotError)
        }
      }
      
      setHasUnsavedChanges(false)
      toast({
        title: "Settings Saved",
        description: "Your agent configuration has been saved successfully."
      })
      
    } catch (error) {
      console.error('Error saving settings:', error)
      toast({
        title: "Save Error",
        description: "Failed to save settings. Please try again.",
        variant: "destructive"
      })
    } finally {
      setIsSaving(false)
    }
  }

  // Activate agent function
  const activateAgent = async () => {
    if (!projectId || !user?.id) return
    
    // Validation checks
    if (!isConnected) {
      toast({
        title: "Reddit Connection Required",
        description: "Please connect your Reddit account first.",
        variant: "destructive"
      })
      return
    }
    
    const approvedSubreddits = subredditCandidates.filter(sub => sub.isApproved)
    if (approvedSubreddits.length === 0) {
      toast({
        title: "Subreddits Required",
        description: "Please approve at least one subreddit for posting.",
        variant: "destructive"
      })
      return
    }
    
    const activeSlots = postingSlots.filter(slot => slot.isActive)
    if (activeSlots.length === 0) {
      toast({
        title: "Schedule Required",
        description: "Please enable at least one posting time slot in your schedule.",
        variant: "destructive"
      })
      return
    }

    // Validate posting frequency matches active slots
    if (agentSettings.postFrequency === 'daily' && activeSlots.length < 7) {
      toast({
        title: "Schedule Mismatch",
        description: "Daily frequency requires all 7 days to be enabled. Please adjust your schedule or change frequency.",
        variant: "destructive"
      })
      return
    }
    
    if (agentSettings.postFrequency === 'weekly' && activeSlots.length > 1) {
      toast({
        title: "Schedule Mismatch", 
        description: "Weekly frequency should have only 1 day enabled. Please adjust your schedule.",
        variant: "destructive"
      })
      return
    }
    
    setIsSaving(true)
    try {
      // Save settings first
      await saveSettings()
      
      // Activate the agent
      const { error } = await supabase
        .from('projects')
        .update({
          enable_reddit_agent: true
        })
        .eq('id', projectId)
      
      if (error) throw error
      
      toast({
        title: "Agent Activated!",
        description: "Your Reddit AI Agent is now active and will start generating content according to your schedule."
      })
      
      // Navigate back to dashboard
      navigate(`/projects/${projectId}/reddit/dashboard`)
      
    } catch (error) {
      console.error('Error activating agent:', error)
      toast({
        title: "Activation Error",
        description: "Failed to activate the agent. Please try again.",
        variant: "destructive"
      })
    } finally {
      setIsSaving(false)
    }
  }

  // Toggle subreddit approval
  const toggleSubredditApproval = async (subredditId: string) => {
    setSubredditCandidates(prev => 
      prev.map(sub => 
        sub.id === subredditId 
          ? { ...sub, isApproved: !sub.isApproved }
          : sub
      )
    )
    setHasUnsavedChanges(true)
  }

  // Update posting slot
  const updatePostingSlot = (index: number, updates: Partial<PostingSlot>) => {
    if (updates.isActive === true) {
      // Check frequency limits before enabling a slot
      const currentActiveCount = postingSlots.filter(s => s.isActive).length
      
      if (agentSettings.postFrequency === 'weekly' && currentActiveCount >= 1) {
        // Weekly: only 1 day allowed, turn off all others first
        setPostingSlots(prev => 
          prev.map((slot, i) => 
            i === index 
              ? { ...slot, ...updates }
              : { ...slot, isActive: false }
          )
        )
      } else if (agentSettings.postFrequency === 'every-other-day' && currentActiveCount >= 4) {
        // Every other day: max 4 days, don't allow more
        toast({
          title: "Schedule Limit Reached",
          description: "Every other day frequency allows maximum 4 days. Please turn off another day first.",
          variant: "destructive"
        })
        return
      } else {
        // Normal update
        setPostingSlots(prev => 
          prev.map((slot, i) => 
            i === index ? { ...slot, ...updates } : slot
          )
        )
      }
    } else {
      // Normal update for disabling or time changes
      setPostingSlots(prev => 
        prev.map((slot, i) => 
          i === index ? { ...slot, ...updates } : slot
        )
      )
    }
    setHasUnsavedChanges(true)
  }

  // Update agent settings
  const updateAgentSettings = (key: keyof AgentSettings, value: any) => {
    setAgentSettings(prev => ({ ...prev, [key]: value }))
    setHasUnsavedChanges(true)
  }

  // Navigation functions
  const goToNextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1)
    }
  }

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const canProceedToNext = () => {
    switch (currentStep) {
      case 1:
        return isConnected
      case 2:
        return subredditCandidates.some(s => s.isApproved)
      case 3:
        return postingSlots.some(s => s.isActive)
      case 4:
        return true
      default:
        return false
    }
  }

  const getDifficultyColor = (difficulty: SubredditCandidate['difficulty']) => {
    switch (difficulty) {
      case 'Easy': return 'bg-green-50 text-green-700 border-green-200'
      case 'Medium': return 'bg-amber-50 text-amber-700 border-amber-200'
      case 'Hard': return 'bg-red-50 text-red-700 border-red-200'
      default: return 'bg-zinc-50 text-zinc-700 border-zinc-200'
    }
  }

  const getValidationStatusColor = (status: SubredditCandidate['validationStatus']) => {
    switch (status) {
      case 'valid': return 'text-green-600'
      case 'insufficient_karma': return 'text-amber-600'
      case 'not_subscribed': return 'text-blue-600'
      case 'pending': return 'text-zinc-600'
      default: return 'text-zinc-600'
    }
  }

  const getValidationStatusIcon = (status: SubredditCandidate['validationStatus']) => {
    switch (status) {
      case 'valid': return <CheckCircle className="h-4 w-4" />
      case 'insufficient_karma': return <AlertCircle className="h-4 w-4" />
      case 'not_subscribed': return <Users className="h-4 w-4" />
      case 'pending': return <RefreshCw className="h-4 w-4 animate-spin" />
      default: return <AlertCircle className="h-4 w-4" />
    }
  }

  return (
    <div className="container py-6 max-w-6xl">
      <div className="flex flex-col space-y-4">
        {/* Setup Progress */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-gradient-to-br from-white via-blue-50/20 to-blue-50/30 dark:from-zinc-900 dark:via-zinc-900/50 dark:to-zinc-800/30 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-8 shadow-sm overflow-hidden relative"
        >
          {/* Background gradient effect */}
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-gradient-to-br from-blue-200/30 to-blue-200/30 dark:from-blue-800/20 dark:to-blue-800/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-gradient-to-tr from-blue-200/30 to-blue-200/30 dark:from-blue-800/20 dark:to-blue-800/20 rounded-full blur-2xl"></div>
          
          <div className="relative">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">
                  Setup Progress
                </h2>
                <p className="text-zinc-600 dark:text-zinc-400 mt-1">Configure your Reddit AI Agent in 4 easy steps</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                  {[
                    isConnected, 
                    subredditCandidates.some(s => s.isApproved), 
                    postingSlots.some(s => s.isActive) && agentSettings.postFrequency !== 'custom', 
                    false
                  ].filter(Boolean).length} of 4 completed
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { step: 1, title: 'Connect Reddit', completed: isConnected, icon: Globe, color: 'from-orange-500 to-red-500' },
                { step: 2, title: 'Configure Subreddits', completed: subredditCandidates.some(s => s.isApproved), icon: Target, color: 'from-blue-500 to-cyan-500' },
                { step: 3, title: 'Set Posting Schedule', completed: postingSlots.some(s => s.isActive) && agentSettings.postFrequency !== 'custom', icon: Calendar, color: 'from-blue-500 to-pink-500' },
                { step: 4, title: 'Activate Agent', completed: false, icon: Bot, color: 'from-green-500 to-emerald-500' }
              ].map((item, index) => (
                <motion.div 
                  key={item.step} 
                  className={`relative rounded-xl p-6 border transition-all duration-300 ${
                    item.completed 
                      ? 'bg-gradient-to-br from-white to-green-50/50 dark:from-zinc-800 dark:to-green-950/30 border-green-200 dark:border-green-800/40 shadow-md' 
                      : currentStep === item.step
                      ? 'bg-gradient-to-br from-white to-blue-50/50 dark:from-zinc-800 dark:to-blue-950/30 border-blue-200 dark:border-blue-800/40 shadow-md'
                      : 'bg-white/60 dark:bg-zinc-800/60 border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600'
                  }`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  whileHover={{ y: -2, transition: { duration: 0.2 } }}
                >
                  <div className="flex flex-col items-center text-center space-y-3">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center relative ${
                      item.completed 
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg' 
                        : currentStep === item.step
                        ? `bg-gradient-to-r ${item.color} text-white shadow-lg`
                        : 'bg-gradient-to-r from-zinc-100 to-zinc-200 dark:from-zinc-700 dark:to-zinc-600 text-zinc-400 dark:text-zinc-500'
                    }`}>
                      {item.completed ? (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Check className="h-6 w-6" />
                        </motion.div>
                      ) : (
                        <item.icon className="h-6 w-6" />
                      )}
                      
                      {/* Step number badge */}
                      <div className={`absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        item.completed 
                          ? 'bg-green-600 text-white' 
                          : currentStep === item.step
                          ? 'bg-blue-600 text-white'
                          : 'bg-zinc-300 dark:bg-zinc-600 text-zinc-600 dark:text-zinc-300'
                      }`}>
                        {item.step}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className={`font-semibold text-sm ${
                        item.completed 
                          ? 'text-green-900 dark:text-green-300' 
                          : currentStep === item.step
                          ? 'text-blue-900 dark:text-blue-300'
                          : 'text-zinc-600 dark:text-zinc-400'
                      }`}>
                        Step {item.step}
                      </h3>
                      <p className={`text-xs mt-1 ${
                        item.completed 
                          ? 'text-green-700 dark:text-green-400' 
                          : currentStep === item.step
                          ? 'text-blue-700 dark:text-blue-400'
                          : 'text-zinc-500 dark:text-zinc-500'
                      }`}>
                        {item.title}
                      </p>
                    </div>
                    
                    {/* Progress indicator */}
                    {item.completed && (
                      <motion.div
                        className="w-full h-1 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: '100%' }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                      />
                    )}
                    {currentStep === item.step && !item.completed && (
                      <div className="w-full h-1 bg-gradient-to-r from-blue-400 to-blue-400 rounded-full animate-pulse" />
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Navigation Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="flex items-center justify-between py-2"
        >
          <div>
            {currentStep > 1 && (
              <Button 
                variant="outline" 
                onClick={goToPreviousStep}
                className="gap-2 border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 hover:border-zinc-400 dark:hover:border-zinc-500 px-6 py-3 font-medium"
                size="lg"
              >
                <ArrowLeft className="h-4 w-4" />
                Previous
              </Button>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            {/* Save Progress Button */}
            {hasUnsavedChanges && currentStep < 4 && (
              <Button 
                variant="outline"
                onClick={saveSettings}
                disabled={isSaving}
                className="gap-2 border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 font-medium"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save Progress
              </Button>
            )}
            
            {currentStep < 4 ? (
              <Button 
                onClick={goToNextStep}
                disabled={!canProceedToNext()}
                className="gap-2 bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700 text-white px-8 py-3 font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
                size="lg"
              >
                Next
                <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button 
                onClick={activateAgent}
                disabled={isSaving || !isConnected || subredditCandidates.filter(s => s.isApproved).length === 0}
                className="gap-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-4 px-12 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                size="lg"
              >
                {isSaving ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <Bot className="h-6 w-6" />
                )}
                {isSaving ? 'Activating...' : 'Activate Reddit AI Agent'}
              </Button>
            )}
          </div>
        </motion.div>

        {/* Main Setup Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="space-y-6">
            {/* Step Content */}
            {currentStep === 1 && (
              <Card className="overflow-hidden border-0 shadow-lg bg-white dark:bg-zinc-800">
                <CardHeader className="bg-gradient-to-r from-orange-50 via-red-50 to-orange-50 dark:from-orange-950/30 dark:via-red-950/30 dark:to-orange-950/30 border-b border-orange-100 dark:border-orange-800/40 relative overflow-hidden">
                  {/* Background decoration */}
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-orange-200/30 to-red-200/30 dark:from-orange-800/20 dark:to-red-800/20 rounded-full blur-2xl"></div>
                  <div className="relative">
                    <CardTitle className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 flex items-center gap-3">
                      <div className="bg-gradient-to-r from-orange-500 to-red-500 p-2 rounded-xl">
                        <Globe className="h-6 w-6 text-white" />
                      </div>
                      Reddit Account Connection
                    </CardTitle>
                    <p className="text-zinc-600 dark:text-zinc-400 mt-2 text-base">Connect your Reddit account to enable autonomous posting</p>
                  </div>
                </CardHeader>
                <CardContent className="p-8">
                  {!isConnected ? (
                    <div className="text-center py-16">
                      <div className="bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/30 dark:to-red-900/30 rounded-3xl w-24 h-24 flex items-center justify-center mx-auto mb-8 shadow-lg">
                        <Globe className="h-12 w-12 text-gradient bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent" />
                      </div>
                      <h3 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mb-4">Connect to Reddit</h3>
                      <p className="text-zinc-600 dark:text-zinc-400 mb-8 max-w-md mx-auto leading-relaxed">
                        Securely connect your Reddit account using OAuth2. We'll never store your password and only request the minimum permissions needed.
                      </p>
                      <Button 
                        onClick={connectReddit}
                        className="gap-3 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white px-10 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                        disabled={isLoadingConnection}
                        size="lg"
                      >
                        {isLoadingConnection ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                        <ExternalLink className="h-5 w-5" />
                        )}
                        Connect Reddit Account
                      </Button>
                      
                      {/* Security info */}
                      <div className="mt-8 bg-zinc-50 dark:bg-zinc-800/50 rounded-xl p-4 max-w-md mx-auto border border-zinc-200 dark:border-zinc-700">
                        <div className="flex items-center gap-2 text-zinc-600 dark:text-zinc-400 text-sm">
                          <Shield className="h-4 w-4 text-green-600 dark:text-green-400" />
                          <span>Secure OAuth2 connection • No password required</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 rounded-2xl p-8 border border-green-200 dark:border-green-800/40 relative overflow-hidden">
                      {/* Success background decoration */}
                      <div className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-green-200/30 to-emerald-200/30 dark:from-green-800/20 dark:to-emerald-800/20 rounded-full blur-2xl"></div>
                      <div className="relative flex items-center gap-6">
                        <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl p-4 shadow-lg">
                          <CheckCircle className="h-10 w-10 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-green-900 dark:text-green-100 mb-2">Successfully Connected!</h3>
                          <div className="flex items-center gap-2">
                            <span className="text-green-700 dark:text-green-300 font-medium">Connected as:</span>
                            <Badge className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-800/40 font-semibold">
                              u/{(redditAccount as any)?.reddit_username || 'username'}
                            </Badge>
                          </div>
                          <p className="text-green-600 dark:text-green-400 text-sm mt-2">Your account is ready for automated posting</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {currentStep === 2 && (
              <Card className="overflow-hidden border-0 shadow-lg bg-white dark:bg-zinc-800">
                <CardHeader className="bg-gradient-to-r from-blue-50 via-cyan-50 to-blue-50 dark:from-blue-950/30 dark:via-cyan-950/30 dark:to-blue-950/30 border-b border-blue-100 dark:border-blue-800/40 relative overflow-hidden">
                  {/* Background decoration */}
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-blue-200/30 to-cyan-200/30 dark:from-blue-800/20 dark:to-cyan-800/20 rounded-full blur-2xl"></div>
                  <div className="relative flex items-center justify-between">
                    <div>
                      <CardTitle className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 flex items-center gap-3">
                        <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-2 rounded-xl">
                          <Target className="h-6 w-6 text-white" />
                        </div>
                        Approved Subreddits
                      </CardTitle>
                      <p className="text-zinc-600 dark:text-zinc-400 mt-2 text-base">Subreddits that your AI agent can post to</p>
                    </div>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        navigate(`/projects/${projectId}/reddit/research`);
                      }}
                      className="gap-2 border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 bg-white/80 dark:bg-zinc-800/80 backdrop-blur-sm"
                    >
                        <Search className="h-4 w-4" />
                      Research & Approve More
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-8">
                  {/* Approved Subreddits List */}
                  {subredditCandidates.filter(s => s.isApproved).length > 0 ? (
                  <div className="space-y-6">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Your Approved Subreddits</h3>
                        <div className="flex items-center gap-3">
                          <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white border-0 px-3 py-1 font-semibold">
                            {subredditCandidates.filter(s => s.isApproved).length} approved
                          </Badge>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              navigate(`/projects/${projectId}/reddit/research`);
                            }}
                            className="gap-2 border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30"
                          >
                            <Plus className="h-4 w-4" />
                            Add More
                          </Button>
                        </div>
                      </div>
                    
                      <div className="grid gap-4">
                        {subredditCandidates.filter(s => s.isApproved).map((subreddit) => (
                          <motion.div 
                            key={subreddit.id} 
                            className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border border-green-200 dark:border-green-800/40 rounded-xl p-6 hover:shadow-md transition-all duration-200"
                            whileHover={{ scale: 1.01 }}
                            transition={{ duration: 0.2 }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md">
                                  r/
                                </div>
                                <div>
                                  <h4 className="font-bold text-zinc-900 dark:text-zinc-100 text-lg">r/{subreddit.name}</h4>
                                  <div className="flex items-center gap-3 mt-2">
                                    <div className={`flex items-center gap-1.5 text-sm ${getValidationStatusColor(subreddit.validationStatus)}`}>
                                      {getValidationStatusIcon(subreddit.validationStatus)}
                                      <span className="capitalize font-medium">{subreddit.validationStatus.replace('_', ' ')}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-3">
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="text-red-600 dark:text-red-400 border-red-200 dark:border-red-800/40 hover:bg-red-50 dark:hover:bg-red-950/30 hover:border-red-300 dark:hover:border-red-700"
                                  onClick={() => toggleSubredditApproval(subreddit.id)}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Remove
                                </Button>
                              </div>
                            </div>
                          </motion.div>
                      ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      <div className="bg-gradient-to-br from-blue-100 to-cyan-100 dark:from-blue-900/30 dark:to-cyan-900/30 rounded-3xl w-24 h-24 flex items-center justify-center mx-auto mb-8 shadow-lg">
                        <Target className="h-12 w-12 text-blue-600 dark:text-blue-400" />
                      </div>
                      <h3 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mb-4">No Approved Subreddits Yet</h3>
                      <p className="text-zinc-600 dark:text-zinc-400 mb-8 max-w-md mx-auto leading-relaxed">
                        You need to research and approve subreddits before your AI agent can start posting. 
                        Use our research tool to find the perfect communities for your content.
                      </p>
                      <Button 
                        onClick={() => {
                          navigate(`/projects/${projectId}/reddit/research`);
                        }}
                        className="gap-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-10 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                        size="lg"
                      >
                        <Search className="h-5 w-5" />
                        Start Subreddit Research
                      </Button>
                  </div>
                  )}
                </CardContent>
              </Card>
            )}

            {currentStep === 3 && (
              <Card className="overflow-hidden border-0 shadow-lg bg-white dark:bg-zinc-800">
                <CardHeader className="bg-gradient-to-r from-blue-50 via-pink-50 to-blue-50 dark:from-blue-950/30 dark:via-pink-950/30 dark:to-blue-950/30 border-b border-blue-100 dark:border-blue-800/40 relative overflow-hidden">
                  {/* Background decoration */}
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-blue-200/30 to-pink-200/30 dark:from-blue-800/20 dark:to-pink-800/20 rounded-full blur-2xl"></div>
                  <div className="relative">
                    <CardTitle className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 flex items-center gap-3">
                      <div className="bg-gradient-to-r from-blue-500 to-pink-500 p-2 rounded-xl">
                        <Calendar className="h-6 w-6 text-white" />
                      </div>
                      Posting Schedule
                    </CardTitle>
                    <p className="text-zinc-600 dark:text-zinc-400 mt-2 text-base">Configure when your AI agent should post content</p>
                  </div>
                </CardHeader>
                <CardContent className="p-8">
                  <div className="space-y-8">
                    {/* Posting Frequency */}
                    <div className="space-y-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-gradient-to-r from-blue-500 to-blue-500 p-2 rounded-lg">
                          <Clock className="h-5 w-5 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Posting Frequency</h3>
                      </div>
                      
                      <Select 
                        value={agentSettings.postFrequency} 
                        onValueChange={(value) => {
                          updateAgentSettings('postFrequency', value)
                          // Auto-enable days based on frequency
                          if (value === 'daily') {
                            // Enable all 7 days
                            setPostingSlots(prev => prev.map(slot => ({ ...slot, isActive: true })))
                          } else if (value === 'every-other-day') {
                            // Enable alternating days (4 days total)
                            setPostingSlots(prev => prev.map((slot, index) => ({ 
                              ...slot, 
                              isActive: index % 2 === 0 && index < 7 
                            })))
                          } else if (value === 'weekly') {
                            // Enable just one day (Monday)
                            setPostingSlots(prev => prev.map((slot, index) => ({ 
                              ...slot, 
                              isActive: index === 0 
                            })))
                          }
                          setHasUnsavedChanges(true)
                        }}
                      >
                        <SelectTrigger className="w-full bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 focus:border-blue-400 focus:ring-blue-400/20 h-12 text-base text-zinc-900 dark:text-zinc-100">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700">
                          <SelectItem value="daily" className="text-zinc-900 dark:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-700">Daily (7 posts per week)</SelectItem>
                          <SelectItem value="every-other-day" className="text-zinc-900 dark:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-700">Every Other Day (3-4 posts per week)</SelectItem>
                          <SelectItem value="weekly" className="text-zinc-900 dark:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-700">Weekly (1 post per week)</SelectItem>
                          <SelectItem value="custom" className="text-zinc-900 dark:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-700">Custom Schedule</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      {/* Helper text explaining smart behavior */}
                      <div className="bg-gradient-to-r from-blue-50 to-blue-50 dark:from-blue-950/30 dark:to-blue-950/30 rounded-xl p-6 border border-blue-200 dark:border-blue-800/40">
                        <div className="flex items-start gap-4">
                          <div className="bg-gradient-to-r from-blue-500 to-blue-500 p-2 rounded-lg shrink-0">
                            <Bot className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <h4 className="font-bold text-blue-900 dark:text-blue-100 text-base mb-2">Smart Content Generation</h4>
                            <p className="text-blue-700 dark:text-blue-300 leading-relaxed">
                              The AI agent intelligently reuses and expands your existing content topics to create fresh Reddit posts. If no topics are available, it generates new ones based on your business analysis.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-2 rounded-lg">
                            <Calendar className="h-5 w-5 text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Weekly Schedule</h3>
                        </div>
                        <Badge className="bg-gradient-to-r from-blue-500 to-pink-500 text-white border-0 px-3 py-1 font-semibold">
                          {postingSlots.filter(slot => slot.isActive).length} of 7 days selected
                        </Badge>
                      </div>
                      
                      <div className="grid gap-4">
                        {postingSlots.map((slot, index) => (
                          <motion.div 
                            key={slot.day} 
                            className={`flex items-center justify-between p-6 rounded-xl border transition-all duration-200 ${
                              slot.isActive 
                                ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-green-200 dark:border-green-800/40 shadow-sm' 
                                : 'bg-zinc-50 dark:bg-zinc-800/50 border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600'
                            }`}
                            whileHover={{ scale: 1.01 }}
                            transition={{ duration: 0.2 }}
                          >
                            <div className="flex items-center gap-6">
                              <Switch 
                                checked={slot.isActive}
                                onCheckedChange={(checked) => updatePostingSlot(index, { isActive: checked })}
                                className="data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-blue-600 data-[state=checked]:to-pink-600"
                              />
                              <div className="flex items-center gap-4">
                                <span className={`font-bold text-lg ${
                                  slot.isActive ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-500 dark:text-zinc-400'
                                }`}>
                                  {slot.day}
                                </span>
                                {agentSettings.postFrequency === 'weekly' && slot.isActive && (
                                  <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-800/40 px-2 py-1 text-xs font-semibold">
                                    Weekly
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <Select 
                                value={slot.time} 
                                onValueChange={(value) => updatePostingSlot(index, { time: value })}
                                disabled={!slot.isActive}
                              >
                                <SelectTrigger className={`w-36 ${
                                  slot.isActive 
                                    ? 'bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 focus:border-green-400 text-zinc-900 dark:text-zinc-100' 
                                    : 'bg-zinc-100 dark:bg-zinc-700/50 border-zinc-200 dark:border-zinc-600 text-zinc-400 dark:text-zinc-500'
                                }`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700">
                                  {Array.from({ length: 24 }, (_, i) => {
                                    const hour = i.toString().padStart(2, '0')
                                    return (
                                      <SelectItem key={`${hour}:00`} value={`${hour}:00`} className="text-zinc-900 dark:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-700">
                                        {hour}:00
                                      </SelectItem>
                                    )
                                  })}
                                </SelectContent>
                              </Select>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {currentStep === 4 && (
              <Card className="overflow-hidden border-0 shadow-lg bg-white dark:bg-zinc-800">
                <CardHeader className="bg-gradient-to-r from-green-50 via-emerald-50 to-green-50 dark:from-green-950/30 dark:via-emerald-950/30 dark:to-green-950/30 border-b border-green-100 dark:border-green-800/40 relative overflow-hidden">
                  {/* Background decoration */}
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-green-200/30 to-emerald-200/30 dark:from-green-800/20 dark:to-emerald-800/20 rounded-full blur-2xl"></div>
                  <div className="relative">
                    <CardTitle className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 flex items-center gap-3">
                      <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-2 rounded-xl">
                        <Bot className="h-6 w-6 text-white" />
                      </div>
                      AI Agent Configuration
                    </CardTitle>
                    <p className="text-zinc-600 dark:text-zinc-400 mt-2 text-base">Configure how your AI agent behaves and operates</p>
                  </div>
                </CardHeader>
                <CardContent className="p-8">
                  <div className="space-y-10">
                    {/* Agent Behavior Settings */}
                    <div className="space-y-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="bg-gradient-to-r from-blue-500 to-blue-500 p-2 rounded-lg">
                          <Settings className="h-5 w-5 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Agent Behavior</h3>
                      </div>
                      
                      <div className="grid gap-6">
                        <motion.div 
                          className="flex items-center justify-between p-6 bg-gradient-to-r from-zinc-50 to-white dark:from-zinc-800/50 dark:to-zinc-700/30 rounded-xl border border-zinc-200 dark:border-zinc-700 hover:shadow-md transition-all duration-200"
                          whileHover={{ scale: 1.01 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="flex items-center gap-4">
                            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-2 rounded-lg">
                              <Eye className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <h4 className="font-bold text-zinc-900 dark:text-zinc-100 text-base">Require Manual Approval</h4>
                              <p className="text-zinc-600 dark:text-zinc-400 text-sm mt-1">Review and approve posts before they're published</p>
                            </div>
                          </div>
                          <Switch 
                            checked={agentSettings.requiresApproval}
                            onCheckedChange={(checked) => updateAgentSettings('requiresApproval', checked)}
                            className="data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-green-600 data-[state=checked]:to-emerald-600"
                          />
                        </motion.div>
                        
                        <motion.div 
                          className="flex items-center justify-between p-6 bg-gradient-to-r from-zinc-50 to-white dark:from-zinc-800/50 dark:to-zinc-700/30 rounded-xl border border-zinc-200 dark:border-zinc-700 hover:shadow-md transition-all duration-200"
                          whileHover={{ scale: 1.01 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="flex items-center gap-4">
                            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-2 rounded-lg">
                              <Users className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <h4 className="font-bold text-zinc-900 dark:text-zinc-100 text-base">Auto-Subscribe to Subreddits</h4>
                              <p className="text-zinc-600 dark:text-zinc-400 text-sm mt-1">Automatically subscribe to approved subreddits if not already subscribed</p>
                            </div>
                          </div>
                          <Switch 
                            checked={agentSettings.autoSubscribe}
                            onCheckedChange={(checked) => updateAgentSettings('autoSubscribe', checked)}
                            className="data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-green-600 data-[state=checked]:to-emerald-600"
                          />
                        </motion.div>
                      </div>
                    </div>

                    {/* Activate Agent */}
                    <div className="bg-gradient-to-br from-blue-50 via-blue-50 to-pink-50 dark:from-blue-950/30 dark:via-blue-950/30 dark:to-pink-950/30 rounded-2xl p-8 border border-blue-200 dark:border-blue-800/40 relative overflow-hidden">
                      {/* Background effects */}
                      <div className="absolute -top-8 -right-8 w-32 h-32 bg-gradient-to-br from-blue-200/30 to-blue-200/30 dark:from-blue-800/20 dark:to-blue-800/20 rounded-full blur-3xl"></div>
                      <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-gradient-to-tr from-blue-200/30 to-pink-200/30 dark:from-blue-800/20 dark:to-pink-800/20 rounded-full blur-2xl"></div>
                      
                      <div className="relative">
                        <div className="flex items-center gap-6 mb-6">
                          <motion.div 
                            className="bg-gradient-to-r from-blue-600 to-blue-600 rounded-2xl p-4 shadow-lg"
                            whileHover={{ scale: 1.05 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Sparkles className="h-8 w-8 text-white" />
                          </motion.div>
                          <div>
                            <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">
                              Ready to Activate!
                            </h3>
                            <p className="text-zinc-700 dark:text-zinc-300 text-lg mt-1">Your AI agent is configured and ready to start generating content</p>
                          </div>
                        </div>
                        
                        {/* Configuration Summary */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                          <div className="bg-white/60 dark:bg-zinc-800/60 backdrop-blur-sm rounded-xl p-4 border border-white/50 dark:border-zinc-700/50">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Reddit Connected</span>
                            </div>
                            <p className="text-xs text-zinc-600 dark:text-zinc-400">u/{(redditAccount as any)?.reddit_username || 'username'}</p>
                          </div>
                          <div className="bg-white/60 dark:bg-zinc-800/60 backdrop-blur-sm rounded-xl p-4 border border-white/50 dark:border-zinc-700/50">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Subreddits</span>
                            </div>
                            <p className="text-xs text-zinc-600 dark:text-zinc-400">{subredditCandidates.filter(s => s.isApproved).length} approved</p>
                          </div>
                          <div className="bg-white/60 dark:bg-zinc-800/60 backdrop-blur-sm rounded-xl p-4 border border-white/50 dark:border-zinc-700/50">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Schedule</span>
                            </div>
                            <p className="text-xs text-zinc-600 dark:text-zinc-400">{postingSlots.filter(s => s.isActive).length} days active</p>
                          </div>
                        </div>
                        
                        <Button 
                          onClick={activateAgent}
                          disabled={isSaving || !isConnected || subredditCandidates.filter(s => s.isApproved).length === 0}
                          className="w-full gap-3 bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700 text-white py-4 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                          size="lg"
                        >
                          {isSaving ? (
                            <Loader2 className="h-6 w-6 animate-spin" />
                          ) : (
                          <Bot className="h-6 w-6" />
                          )}
                          {isSaving ? 'Activating...' : 'Activate Reddit AI Agent'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  )
} 